import type { TeamRegistrationField } from '@/types/tournament/game';
import type {
  PhoneData,
  PlayerForm,
} from '@/types/tournament/quickRegistration';
import type { User } from '@/types/auth';
import type { TeamMember } from '@/types/team';

// Re-export field helpers for convenience
export {
  getActiveFields,
  getFieldMandatoryStatus,
  getActiveGeneralFields,
  getGeneralFieldMandatoryStatus,
} from './fieldHelpers';

/**
 * Standard TeamMembers field names that can be mapped
 * Matches backend STANDARD_TEAM_MEMBER_FIELDS
 */
export const STANDARD_TEAM_MEMBER_FIELDS = [
  'name',
  'email',
  'mobileNo',
  'inGameName',
  'inGameId',
] as const;

export type StandardTeamMemberField =
  (typeof STANDARD_TEAM_MEMBER_FIELDS)[number];

/**
 * Standard General Form field keys
 * Use these constants instead of hardcoded strings
 */
export const STANDARD_GENERAL_FORM_FIELD_KEYS = {
  CLAN_NAME: 'clanName',
  TEAM_NAME: 'teamName',
} as const;

/**
 * Converts dynamic registration form data to API payload structure
 * IMPORTANT: The BE validation expects all fieldKeys as top-level keys in the payload.
 * The BE will handle moving custom fields into customFields array.
 *
 * @param formData - Form data with fieldKey as keys
 * @param teamRegistrationFields - Field configurations from database
 * @returns API payload with all fields as top-level keys (no customFields array)
 */
export const buildRegistrationPayload = (
  formData: Record<string, string | PhoneData>,
  teamRegistrationFields: TeamRegistrationField[],
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  // Process each field from configuration
  // Note: Field filtering by active/inactive is handled at component level via roleValidationRules
  for (const field of teamRegistrationFields) {
    const value = formData[field.fieldKey];

    // Skip if value is not provided
    if (value === undefined || value === null || value === '') {
      continue;
    }

    // Handle phone number field specially
    // Backend expects 'mobileNo' in payload
    if (field.fieldKey === 'mobileNo') {
      // Handle phone object structure
      if (typeof value === 'object' && 'number' in value) {
        // Skip if phone number is empty
        const phoneNumber = String(value.number || '').trim();
        if (!phoneNumber) {
          continue;
        }
        result.mobileNo = {
          code: value.code || '',
          number: phoneNumber,
          countryName: value.countryName || undefined,
          abbreviation: value.abbreviation || undefined,
        };
      } else {
        // If phone is just a string, check if it's not empty
        const phoneStr = String(value || '').trim();
        if (!phoneStr) {
          continue;
        }
        result.mobileNo = {
          code: '',
          number: phoneStr,
        };
      }
    } else {
      if (typeof value === 'object' && 'number' in value) {
        // This shouldn't happen for non-phone fields, but handle it
        result[field.fieldKey] = JSON.stringify(value);
      } else {
        // For custom fields and standard fields, include the value
        const stringValue = String(value).trim();
        if (stringValue) {
          result[field.fieldKey] = stringValue;
        }
      }
    }
  }

  return result;
};

/**
 * Builds form data from user data for active fields only
 * Maps user properties to fieldKeys based on standard field mappings
 * Only includes fields that have actual values
 *
 * @param user - User object with profile data
 * @param activeFields - Fields that are active for the current role/context
 * @returns Form data object with fieldKey as keys
 */
export const buildFormDataFromUser = (
  user: User | null | undefined,
  activeFields: TeamRegistrationField[],
): Record<string, string | PhoneData> => {
  const formData: Record<string, string | PhoneData> = {};

  if (!user) return formData;

  // Field mapping: fieldKey -> user property extractor
  const fieldMappers: Record<
    string,
    (user: User) => string | PhoneData | null | undefined
  > = {
    email: user => user.email || null,
    name: user => user.name || user.username || null,
    mobileNo: user => {
      if (!user.mobileNumber) return null;
      return {
        code: user.countryCode?.code || '',
        number: user.mobileNumber,
        countryName: user.countryCode?.name || null,
        abbreviation: user.countryCode?.abbreviation || null,
      };
    },
    inGameName: user => user.username || user.name || null,
    inGameId: () => null, // Always skip inGameId from user data (user doesn't have it)
  };

  for (const field of activeFields) {
    const mapper = fieldMappers[field.fieldKey];
    if (mapper) {
      const value = mapper(user);
      if (value !== null && value !== undefined) {
        formData[field.fieldKey] = value;
      }
    }
    // Custom fields are skipped (we don't have user data for them)
  }

  return formData;
};

/**
 * Builds form data from PlayerForm state - includes all standard and dynamic fields
 * Used for building payloads for players and substitutes
 *
 * @param playerData - PlayerForm object with standard and dynamic fields
 * @param excludeKeys - Optional array of keys to exclude (e.g., 'isCaptain')
 * @returns Form data object with fieldKey as keys
 */
export const buildFormDataFromPlayer = (
  playerData: PlayerForm,
  excludeKeys: string[] = ['isCaptain'],
): Record<string, string | PhoneData> => {
  const formData: Record<string, string | PhoneData> = {
    name: playerData.name,
    email: playerData.email,
    inGameName: playerData.inGameName,
    inGameId: playerData.inGameId,
  };
  if (playerData.phone) {
    formData.mobileNo = playerData.phone;
  }

  // Include all dynamic fields from player state
  for (const key in playerData) {
    if (
      ![
        'name',
        'email',
        'inGameName',
        'inGameId',
        'phone',
        ...excludeKeys,
      ].includes(key)
    ) {
      const value = playerData[key];
      if (value !== undefined && value !== null && value !== '') {
        // Skip boolean values (like isCaptain)
        if (typeof value !== 'boolean') {
          formData[key] = value as string | PhoneData;
        }
      }
    }
  }

  return formData;
};

/**
 * Prefill registration form fields from an existing team roster member (edit flow).
 * Standard keys map to top-level `TeamMember` props; anything else comes from `customFields`
 * (same shape the API uses for dynamic registration fields — see backend `convertPayloadToTeamMember`).
 */
export const buildFormDataFromTeamMember = (
  member: TeamMember,
  activeFields: TeamRegistrationField[],
): Record<string, string | PhoneData> => {
  const formData: Record<string, string | PhoneData> = {};

  const customByKey = new Map<string, string>();
  if (Array.isArray(member.customFields)) {
    for (const entry of member.customFields) {
      if (
        entry &&
        typeof entry === 'object' &&
        'fieldKey' in entry &&
        'value' in entry
      ) {
        customByKey.set(
          String((entry as { fieldKey: string }).fieldKey),
          String((entry as { value: unknown }).value ?? ''),
        );
      }
    }
  }

  for (const field of activeFields) {
    const key = field.fieldKey;
    if (key === 'mobileNo') {
      const m = member.mobileNo;
      if (m?.number != null && String(m.number).trim() !== '') {
        formData[key] = {
          code: m.code || '',
          number: String(m.number),
          countryName: m.name ?? null,
          abbreviation: m.abbreviation ?? null,
        };
      }
    } else if (
      (STANDARD_TEAM_MEMBER_FIELDS as readonly string[]).includes(key)
    ) {
      const raw = member[key as keyof TeamMember];
      if (raw !== undefined && raw !== null && raw !== '') {
        if (typeof raw === 'string' || typeof raw === 'number') {
          formData[key] = String(raw);
        }
      }
    } else {
      const v = customByKey.get(key);
      if (v !== undefined && v !== '') {
        formData[key] = v;
      }
    }
  }

  return formData;
};
